/**
 * The arrow editors: the panel for a selected arrow (EdgeInspector) and the
 * list of a node's outgoing arrows inside the node panel (NodeConnections).
 *
 * Both write through the model's edge edits (updateEdge, connectNodes,
 * removeEdges), which refuse a self loop, a duplicate arrow and a dangling end,
 * so the pickers only have to leave out the choices the model would refuse. Every picker
 * is the widgets' own pick-one list (`../combo/`), never a native select.
 */
import { useId, type ReactElement } from "react";
import { Combo } from "../combo/index.js";
import type { FlowSelection } from "./canvasModel.js";
import { nodeTitle } from "./canvasModel.js";
import { IconTrash } from "./icons.js";
import { CommitText, Field, IconButton, Section, nodeOptions, type EditorEnv } from "./inspectorFields.js";
import { FLOW_EDGE_KIND_LABEL } from "./labels.js";
import {
  FLOW_EDGE_KINDS,
  FLOW_LIMITS,
  connectNodes,
  readText,
  removeEdges,
  updateEdge,
  writeText,
  type FlowEdge,
  type FlowEdgeKind,
  type FlowNode,
} from "./model.js";

/** The stored kind for a picked option: "default" is stored as no kind at all. */
function kindOf(value: string): FlowEdgeKind | null {
  return value !== "default" && (FLOW_EDGE_KINDS as readonly string[]).includes(value) ? (value as FlowEdgeKind) : null;
}

/** Writes an arrow's label in the editing language; emptying it drops that language (or the label). */
function labelWriter(env: EditorEnv, id: string): (value: string) => void {
  return (value) => {
    env.edit((d) => {
      const cur = d.edges.find((e) => e.id === id);
      return cur ? updateEdge(d, id, { label: writeText(cur.label, value.trim(), env.textLocale, env.textDefaultLocale) }) : d;
    });
  };
}

function KindSelect({ env, edge, id }: { env: EditorEnv; edge: FlowEdge; id?: string }): ReactElement {
  return (
    <Combo
      id={id}
      className="wa-fm__pickw wa-fm__pickw--fit"
      buttonClassName="wa-fm__pick"
      fitPanel
      label={env.labels.edgeKind}
      value={edge.kind ?? "default"}
      choices={FLOW_EDGE_KINDS.map((k) => ({ value: k, label: env.labels[FLOW_EDGE_KIND_LABEL[k]] }))}
      onChange={(value) => {
        const kind = kindOf(value);
        env.edit((d) => updateEdge(d, edge.id, { kind }));
      }}
    />
  );
}

/** Where the arrow points. Nodes this source already points at are left out (one arrow per pair). */
function TargetSelect({ env, edge, id }: { env: EditorEnv; edge: FlowEdge; id?: string }): ReactElement {
  const others = nodeOptions(env.doc, env.locale, env.labels, edge.source);
  const taken = new Set(env.doc.edges.filter((e) => e.source === edge.source && e.id !== edge.id).map((e) => e.target));
  return (
    <Combo
      id={id}
      className="wa-fm__pickw"
      buttonClassName="wa-fm__pick"
      fitPanel
      label={env.labels.target}
      value={edge.target}
      choices={others.filter((o) => !taken.has(o.id)).map((o) => ({ value: o.id, label: o.title }))}
      searchLabel={env.labels.pickSearch}
      emptyText={env.labels.pickNoMatch}
      onChange={(target) => env.edit((d) => updateEdge(d, edge.id, { target }))}
    />
  );
}

export function EdgeInspector({
  env,
  edge,
  onSelect,
  onDeleted,
}: {
  env: EditorEnv;
  edge: FlowEdge;
  onSelect: (sel: FlowSelection) => void;
  onDeleted: () => void;
}): ReactElement {
  const ids = useId();
  const { labels } = env;
  const source = env.doc.nodes.find((n) => n.id === edge.source);
  return (
    <>
      <div className="wa-fm__field">
        <span className="wa-fm__label">{labels.colFrom}</span>
        <button type="button" className="wa-fm__jump" title={labels.goToNode} onClick={() => onSelect({ type: "node", id: edge.source })}>
          <span className="wa-fm__jump-text">{source ? nodeTitle(source, env.locale, labels) : labels.untitled}</span>
        </button>
      </div>
      <Field label={labels.target} htmlFor={`${ids}-target`}>
        <TargetSelect env={env} edge={edge} id={`${ids}-target`} />
      </Field>
      <Field label={labels.edgeLabel} htmlFor={`${ids}-label`}>
        <CommitText id={`${ids}-label`} value={readText(edge.label, env.locale)} maxLength={FLOW_LIMITS.label} onCommit={labelWriter(env, edge.id)} />
      </Field>
      <Field label={labels.edgeKind} htmlFor={`${ids}-kind`}>
        <KindSelect env={env} edge={edge} id={`${ids}-kind`} />
      </Field>
      <button
        type="button"
        className="wa-fm__btn wa-fm__btn--danger wa-fm__btn--block"
        onClick={() => {
          if (env.edit((d) => removeEdges(d, [edge.id]))) onDeleted();
        }}
      >
        <IconTrash size={15} />
        {labels.deleteEdge}
      </button>
    </>
  );
}

/** A node's outgoing arrows (target, label, kind, remove) and a picker that adds one more. */
export function NodeConnections({ env, node }: { env: EditorEnv; node: FlowNode }): ReactElement {
  const { doc, labels } = env;
  const outgoing = doc.edges.filter((e) => e.source === node.id);
  const targeted = new Set(outgoing.map((e) => e.target));
  const free = nodeOptions(doc, env.locale, labels, node.id).filter((o) => !targeted.has(o.id));
  const canAdd = free.length > 0 && doc.edges.length < FLOW_LIMITS.edges;
  return (
    <Section title={labels.connections}>
      {outgoing.length ? (
        <ul className="wa-fm__rows">
          {outgoing.map((e) => (
            <li key={e.id} className="wa-fm__card">
              <div className="wa-fm__row">
                <span className="wa-fm__row-label">{labels.target}</span>
                <TargetSelect env={env} edge={e} />
              </div>
              <div className="wa-fm__row">
                <CommitText
                  value={readText(e.label, env.locale)}
                  ariaLabel={labels.edgeLabel}
                  placeholder={labels.edgeLabel}
                  maxLength={FLOW_LIMITS.label}
                  onCommit={labelWriter(env, e.id)}
                />
                <KindSelect env={env} edge={e} />
                <IconButton label={labels.deleteEdge} danger onClick={() => env.edit((d) => removeEdges(d, [e.id]))}>
                  <IconTrash size={14} />
                </IconButton>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      {canAdd ? (
        <Combo
          className="wa-fm__pickw"
          buttonClassName="wa-fm__pick"
          fitPanel
          label={labels.addConnection}
          placeholder={labels.addConnection}
          value=""
          choices={free.map((o) => ({ value: o.id, label: o.title }))}
          searchLabel={labels.pickSearch}
          emptyText={labels.pickNoMatch}
          onChange={(target) => env.edit((d) => connectNodes(d, { source: node.id, target }, { random: true }).doc)}
        />
      ) : null}
    </Section>
  );
}
